// ABOUTME: Multi-step wizard for self-service Databricks data source connection.
// ABOUTME: Guides users through credentials, testing, configuration, and completion.

import { useState, useCallback } from 'react';
import { WelcomeStep } from './steps/WelcomeStep';
import { CredentialsStep } from './steps/CredentialsStep';
import { TestingStep } from './steps/TestingStep';
import { ConfigureStep } from './steps/ConfigureStep';
import { CompleteStep } from './steps/CompleteStep';
import {
  dataSourceApi,
  type ConnectionCredentials,
  type TestConnectionResult,
} from '@/services/api/dataSourceClient';
import { ApiError } from '@/services/api/client';
import type { DataSource } from '@/types/api';

type WizardStep = 'welcome' | 'credentials' | 'testing' | 'configure' | 'complete';

interface WizardState {
  step: WizardStep;
  connectionDetails: ConnectionCredentials;
  testResult: TestConnectionResult | null;
  configuration: {
    name: string;
    catalog: string;
    schema: string;
  };
  createdDataSource: DataSource | null;
}

interface DataSourceWizardProps {
  onComplete: (dataSource: DataSource) => void;
  onCancel: () => void;
}

const STEP_NUMBERS: Record<WizardStep, number> = {
  welcome: 1,
  credentials: 2,
  testing: 3,
  configure: 4,
  complete: 5,
};

const TOTAL_STEPS = 5;

export function DataSourceWizard({ onComplete, onCancel }: DataSourceWizardProps) {
  const [state, setState] = useState<WizardState>({
    step: 'welcome',
    connectionDetails: {
      workspaceUrl: '',
      httpPath: '',
      accessToken: '',
    },
    testResult: null,
    configuration: {
      name: '',
      catalog: '',
      schema: '',
    },
    createdDataSource: null,
  });

  const [isLoading, setIsLoading] = useState(false);

  // Navigation handlers
  const goToStep = useCallback((step: WizardStep) => {
    setState((prev) => ({ ...prev, step }));
  }, []);

  // Handle credentials submission and test connection
  const handleCredentialsSubmit = useCallback(async (credentials: ConnectionCredentials) => {
    setState((prev) => ({
      ...prev,
      connectionDetails: credentials,
      step: 'testing',
    }));

    setIsLoading(true);
    try {
      const result = await dataSourceApi.testConnection(credentials);
      setState((prev) => ({
        ...prev,
        testResult: result,
      }));
    } catch (error) {
      setState((prev) => ({
        ...prev,
        testResult: {
          success: false,
          error: error instanceof Error ? error.message : 'Connection test failed',
        },
      }));
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Handle retry connection test
  const handleRetryTest = useCallback(async () => {
    setIsLoading(true);
    setState((prev) => ({ ...prev, testResult: null }));

    try {
      const result = await dataSourceApi.testConnection(state.connectionDetails);
      setState((prev) => ({
        ...prev,
        testResult: result,
      }));
    } catch (error) {
      setState((prev) => ({
        ...prev,
        testResult: {
          success: false,
          error: error instanceof Error ? error.message : 'Connection test failed',
        },
      }));
    } finally {
      setIsLoading(false);
    }
  }, [state.connectionDetails]);

  // Handle data source creation
  const handleCreateDataSource = useCallback(async (config: { name: string; catalog: string; schema: string }) => {
    setIsLoading(true);

    try {
      const dataSource = await dataSourceApi.createDataSource({
        name: config.name,
        type: 'databricks',
        config: {
          host: state.connectionDetails.workspaceUrl,
          http_path: state.connectionDetails.httpPath,
          catalog: config.catalog,
          schema: config.schema,
        },
        credentials: {
          access_token: state.connectionDetails.accessToken,
        },
      });

      setState((prev) => ({
        ...prev,
        configuration: config,
        createdDataSource: dataSource,
        step: 'complete',
      }));
    } catch (error) {
      console.error('Failed to create data source:', error);
      let message = 'Failed to save data source. Please try again.';
      if (error instanceof ApiError) {
        if (error.status === 402) {
          message = 'You have reached the maximum number of data sources for your plan.';
        } else {
          message = error.message || message;
        }
      } else if (error instanceof TypeError && (error.message.includes('fetch') || error.message.includes('network'))) {
        message = 'Unable to connect to the server. Please check your internet connection.';
      }
      setState((prev) => ({
        ...prev,
        testResult: {
          success: false,
          message,
          error: message,
        },
        step: 'testing',
      }));
    } finally {
      setIsLoading(false);
    }
  }, [state.connectionDetails]);

  // Handle completion
  const handleComplete = useCallback(() => {
    if (state.createdDataSource) {
      onComplete(state.createdDataSource);
    }
  }, [state.createdDataSource, onComplete]);

  const currentStepNumber = STEP_NUMBERS[state.step];

  return (
    <div className="bg-surface-0 rounded-xl shadow-lg max-w-lg w-full mx-auto">
      {/* Step indicator */}
      <div className="px-6 pt-6 pb-4 border-b border-surface-100">
        <div className="flex items-center justify-between mb-3">
          <span className="text-sm font-medium text-surface-500">
            Step {currentStepNumber} of {TOTAL_STEPS}
          </span>
        </div>
        <div className="flex gap-1">
          {[...Array(TOTAL_STEPS)].map((_, i) => (
            <div
              key={i}
              className={`h-1 flex-1 rounded-full transition-colors ${
                i < currentStepNumber
                  ? 'bg-primary-500'
                  : 'bg-surface-300'
              }`}
            />
          ))}
        </div>
      </div>

      {/* Step content */}
      <div className="min-h-[400px]">
        {state.step === 'welcome' && (
          <WelcomeStep
            onNext={() => goToStep('credentials')}
            onCancel={onCancel}
          />
        )}

        {state.step === 'credentials' && (
          <CredentialsStep
            initialCredentials={state.connectionDetails}
            onNext={handleCredentialsSubmit}
            onBack={() => goToStep('welcome')}
            onCancel={onCancel}
          />
        )}

        {state.step === 'testing' && (
          <TestingStep
            isLoading={isLoading}
            testResult={state.testResult}
            onRetry={handleRetryTest}
            onNext={() => goToStep('configure')}
            onBack={() => goToStep('credentials')}
            onCancel={onCancel}
          />
        )}

        {state.step === 'configure' && (
          <ConfigureStep
            catalogs={state.testResult?.catalogs || []}
            schemas={state.testResult?.schemas || []}
            initialConfig={state.configuration}
            isLoading={isLoading}
            onSubmit={handleCreateDataSource}
            onBack={() => goToStep('testing')}
            onCancel={onCancel}
          />
        )}

        {state.step === 'complete' && state.createdDataSource && (
          <CompleteStep
            dataSource={state.createdDataSource}
            onComplete={handleComplete}
          />
        )}
      </div>
    </div>
  );
}
